import { useMutation, useSubscription } from '@apollo/client';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import Table from '@risksmart-app/components/src/table';
import {
  Data_Import_Status_Enum,
  DataImportStartImportDocument,
  GetDataImportStatusDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import TabHeader from 'src/components/tab-header';

import { useGetDataImportErrors } from '@/hooks/queries';

import { useGetCollectionTableProps } from './config';

const Tab: FC = () => {
  const dataImportId = useGetGuidParam('dataImportId');
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'dataImportResult',
  });

  const { data: dataImportData, loading: dataImportLoading } = useSubscription(
    GetDataImportStatusDocument,
    {
      variables: { id: dataImportId },
    }
  );

  const dataImportStatus = dataImportData?.data_import[0].Status;
  const [startImport, startImportResult] = useMutation(
    DataImportStartImportDocument,
    {
      variables: { Id: dataImportId },
    }
  );

  const { data, loading, refetch } = useGetDataImportErrors({
    queryArgs: { dataImportId },
    shouldSkip: dataImportStatus !== Data_Import_Status_Enum.Failed,
  });

  useEffect(() => {
    if (dataImportStatus === Data_Import_Status_Enum.Failed) {
      refetch();
    }
  }, [dataImportStatus, refetch]);

  const { getByValue } = useRating('data_import_status');
  const tableProps = useGetCollectionTableProps(data?.data_import_error);
  const hasErrors = (data?.data_import_error?.length ?? 0) > 0;

  return (
    <Table
      header={
        <SpaceBetween size={'m'}>
          <SimpleRatingBadge
            data-testid={'dataImportStatus'}
            rating={getByValue(dataImportStatus)}
          />
          <TabHeader
            actions={
              dataImportStatus === Data_Import_Status_Enum.Valid ? (
                <Button
                  disabled={startImportResult.loading || dataImportLoading}
                  onClick={() => startImport()}
                >
                  {st('startImport')}
                </Button>
              ) : (
                hasErrors && (
                  <ExportButton
                    tableProps={tableProps}
                    entityLabel={st('exportEntityLabel')}
                  />
                )
              )
            }
          >
            {st('tabTitle')}
          </TabHeader>
        </SpaceBetween>
      }
      variant={'embedded'}
      {...tableProps}
      loading={loading}
    />
  );
};

export default Tab;
